import React, { useState } from 'react';
import * as XLSX from 'xlsx';

const SmartResultsSection = ({ tableResults = [], isDataLoaded = false }) => {
  const [selectedTable, setSelectedTable] = useState(null);

  // Note: tableResults now contains transactions array instead of table data
  const transactions = Array.isArray(tableResults) ? tableResults : [];

  const exportTransactionsToExcel = () => {
    if (!transactions || transactions.length === 0) {
      alert('No transactions to export');
      return;
    }

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(transactions);
    const wb = XLSX.utils.book_new();
    
    // Set column widths based on content
    const colWidths = [
      { wch: 12 }, // date
      { wch: 30 }, // description
      { wch: 15 }, // debit
      { wch: 15 }, // credit
      { wch: 15 }, // balance
      { wch: 20 }  // source_file
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Bank_Transactions');

    // Generate filename with current date
    const currentDate = new Date().toISOString().split('T')[0];
    const filename = `bank_statement_transactions_${currentDate}.xlsx`;

    // Export file
    XLSX.writeFile(wb, filename);
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Group transactions by source file
  const groupedTransactions = transactions.reduce((groups, transaction) => {
    const file = transaction.source_file || 'Unknown';
    if (!groups[file]) {
      groups[file] = [];
    }
    groups[file].push(transaction);
    return groups;
  }, {});

  if (!isDataLoaded || transactions.length === 0) {
    return (
      <section className="mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 overflow-hidden transition-colors duration-300">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-800 dark:text-white transition-colors duration-300">Bank Statement Transactions</h3>
          </div>
          <div className="px-6 py-16 text-center text-gray-500 dark:text-gray-400 transition-colors duration-300">
            <div className="max-w-sm mx-auto">
              <div className="mb-4">
                <i className="fa-solid fa-receipt text-6xl text-gray-300 dark:text-gray-600"></i>
              </div>
              <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
                No Transactions Extracted Yet
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">
                Upload bank statement files to automatically extract transaction data using AI.
              </p>
              <div className="inline-flex items-center text-xs text-gray-400 dark:text-gray-500 transition-colors duration-300">
                <i className="fa-solid fa-magic-wand-sparkles mr-2"></i>
                <span>AI bank statement parser ready</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const totalFiles = Object.keys(groupedTransactions).length;
  const totalDebit = transactions.reduce((sum, txn) => sum + (txn.debit || 0), 0);
  const totalCredit = transactions.reduce((sum, txn) => sum + (txn.credit || 0), 0);

  return (
    <section className="mb-8">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 text-center transition-colors duration-300">
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 transition-colors duration-300">{totalFiles}</div>
          <div className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">Files Processed</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 text-center transition-colors duration-300">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400 transition-colors duration-300">{transactions.length}</div>
          <div className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">Transactions</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 text-center transition-colors duration-300">
          <div className="text-lg font-bold text-red-600 dark:text-red-400 transition-colors duration-300">{formatCurrency(totalDebit)}</div>
          <div className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">Total Debit</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 text-center transition-colors duration-300">
          <div className="text-lg font-bold text-blue-600 dark:text-blue-400 transition-colors duration-300">{formatCurrency(totalCredit)}</div>
          <div className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">Total Credit</div>
        </div>
      </div>

      {/* Transaction Results */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 overflow-hidden transition-colors duration-300">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center transition-colors duration-300">
          <h3 className="text-lg font-medium text-gray-800 dark:text-white transition-colors duration-300">Bank Statement Transactions</h3>
          <div className="flex items-center space-x-3">
            <div className="flex items-center text-xs text-green-600 dark:text-green-400 transition-colors duration-300">
              <i className="fa-solid fa-check-circle mr-1"></i>
              <span>AI Processing Complete</span>
            </div>
            <button 
              onClick={exportTransactionsToExcel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
            >
              <i className="fa-solid fa-file-export mr-1"></i> Export Transactions
            </button>
          </div>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {Object.entries(groupedTransactions).map(([filename, fileTransactions], index) => (
            <div key={index} className="p-6">
              {/* File Header */}
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setSelectedTable(selectedTable === index ? null : index)}
              >
                <div className="flex items-center">
                  <i className="fa-solid fa-file-lines text-2xl text-purple-600 dark:text-purple-400 mr-4 transition-colors duration-300"></i>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white transition-colors duration-300">
                      {filename}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
                      {fileTransactions.length} transactions • 
                      Debit: {formatCurrency(fileTransactions.reduce((sum, txn) => sum + (txn.debit || 0), 0))} • 
                      Credit: {formatCurrency(fileTransactions.reduce((sum, txn) => sum + (txn.credit || 0), 0))}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <i className={`fa-solid ${selectedTable === index ? 'fa-chevron-up' : 'fa-chevron-down'} text-gray-400 dark:text-gray-500 transition-colors duration-300`}></i>
                </div>
              </div>

              {/* Transaction Details */}
              {selectedTable === index && fileTransactions.length > 0 && (
                <div className="mt-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-300">
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3">Description</th>
                          <th className="px-4 py-3 text-right">Debit</th>
                          <th className="px-4 py-3 text-right">Credit</th>
                          <th className="px-4 py-3 text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {fileTransactions.slice(0, 20).map((transaction, txnIndex) => (
                          <tr key={txnIndex} className={txnIndex % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700/50'}>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">
                              {transaction.date}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">
                              {transaction.description}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-red-600 dark:text-red-400 transition-colors duration-300">
                              {transaction.debit ? formatCurrency(transaction.debit) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-green-600 dark:text-green-400 transition-colors duration-300">
                              {transaction.credit ? formatCurrency(transaction.credit) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300 font-medium transition-colors duration-300">
                              {transaction.balance ? formatCurrency(transaction.balance) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {fileTransactions.length > 20 && (
                      <div className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 transition-colors duration-300">
                        Showing 20 of {fileTransactions.length} transactions
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center transition-colors duration-300">
          <div className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
            Extracted {transactions.length} transactions from {totalFiles} file(s) using AI
          </div>
        </div>
      </div>
    </section>
  );
};

export default SmartResultsSection; 